import {
	ChangeEvent,
	FC,
	isValidElement,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
	DataGrid,
	GridColDef,
	GridColumnVisibilityModel,
	GridSortModel,
	GridValidRowModel,
	useGridApiRef
} from '@mui/x-data-grid';
import { roRO } from '@mui/x-data-grid/locales';
import InboxOutlined from '@ant-design/icons/InboxOutlined';
import { TableCardWrap } from './styled';
import { CustomTablePagination } from './table-pagination/component';
import { CellOverflowTooltip } from './cell-tooltip';
import { ColumnMenuWithPinning, PinnedColumns, PinnedSide } from './column-pinning';
import { cellTextOf, reactNodeComparator } from './sorting';
import { sanitizeTableConfig, useTableConfig } from './use-table-config';
import { IColumn, ICustomTableProps } from './types';

// ==============================|| GLOBAL TABLE (MUI X DataGrid based) ||============================== //
// The global table is built on the MIT DataGrid with its free features enabled: header
// sorting (server-side via `sort`/`onSort`, client-side otherwise), column resizing,
// left/right column pinning (driven through the grid's internal state — see
// column-pinning.tsx), the column menu (hide / manage columns), row virtualization and
// the Romanian locale. The external contract (ICustomTableProps) is unchanged: filters
// and the active-filters section stay in consumer pages (they drive the BE query), and
// the custom pagination (TableFetchPagination / MantisPagination) plus the Mantis look
// remain ours — which is why the grid's own filtering and footer are disabled.
// See README.md in this directory for the full specification and usage guide.

// The noRowsOverlay slot receives the page's message via slotProps (official MUI X augmentation).
declare module '@mui/x-data-grid' {
	interface NoRowsOverlayPropsOverrides {
		message?: ReactNode;
	}
}

const { localeText } = roRO.components.MuiDataGrid.defaultProps;

// Module-level guard for the server-sort replay: two mounts in quick succession
// (StrictMode, duplicated tables on the same key) must not replay onSort twice — the
// second replay would flip the restored direction and trigger an extra fetch.
const restoredSortAt = new Map<string, number>();
const RESTORE_REPLAY_WINDOW_MS = 1000;

// Minimum width of proportional columns (below it the table switches to horizontal
// scrolling — which is where left/right pinning becomes useful).
const MIN_COLUMN_WIDTH = 140;

// Floor used while the table is PRISTINE (no manual resize, no extra columns enabled,
// no pinning): the default column set must fit the container with no horizontal
// scrollbar — columns squeeze (headers/cells ellipsize) instead of overflowing. The
// comfortable MIN_COLUMN_WIDTH only kicks in once the user interacts, where scrolling
// is expected. Kept > 0 so ultra-narrow viewports (phones) still get a usable scroll.
const PRISTINE_MIN_COLUMN_WIDTH = 60;

// A pinned column keeps its normal width and only grows when its cell content requires
// it, never beyond this cap — an overly wide pinned column would cover the rest.
const PINNED_MAX_WIDTH = 380;

// Manual resize (separator drag) bounds: below the minimum the drag stops (a column
// cannot be visually "deleted" — hiding is done from the column menu only), above the
// maximum it stops widening (it would cover the rest of the table).
const MIN_RESIZE_WIDTH = 140;
const MAX_RESIZE_WIDTH = 400;

// The actions column is kept compact: its proportional share is clamped to this range
// (buttons are rendered small, on a single row) and the freed space goes back to the
// content columns.
const ACTIONS_MIN_WIDTH = 96;
const ACTIONS_MAX_WIDTH = 168;

// Stable row id accessor. `__rowId` is injected in gridRows (see below).
const getRowId = (row: GridValidRowModel): number => row.__rowId as number;

// "No data" overlay — icon + the page's message (via slotProps), centered. It carries
// its own minHeight: the overlay's inner wrapper can have a collapsed height, in which
// case vertical centering has nothing to align against and the icon circle would be
// squeezed (flex-shrink) into an oval.
// React 19 ignores `defaultProps` on function components — the `??` fallback below is
// the actual default, so the legacy rule is silenced instead of adding dead code.
// eslint-disable-next-line react/require-default-props
const NoRowsOverlay: FC<{ message?: ReactNode }> = ({ message = undefined }) => (
	<Stack
		sx={{
			height: '100%',
			minHeight: 220,
			alignItems: 'center',
			justifyContent: 'center',
			gap: 1.5,
			py: 4
		}}
	>
		<Box
			sx={{
				width: 56,
				height: 56,
				flexShrink: 0,
				borderRadius: '50%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				bgcolor: 'grey.100',
				color: 'text.secondary',
				fontSize: 26,
				'& .anticon': { display: 'flex' }
			}}
		>
			<InboxOutlined />
		</Box>
		<Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
			{message ?? 'Nu sunt date disponibile'}
		</Typography>
	</Stack>
);

// IColumn → GridColDef. Percentage widths become px computed by us against the real
// container width — NOT `flex`: the DataGrid flex hydration enters a measurement loop
// on some layouts ("Maximum update depth exceeded" — mui-x #15837 / #12851).
// Every cell (except actions) is wrapped in CellOverflowTooltip: when the content is
// actually cropped, a tooltip shows the text extracted from the cell. Plain strings
// render as single-line ellipsized Typography inside the same wrapper.
// Sorting — ONLY on columns explicitly marked `sortable: true` (others get no icon):
// · table with sort/onSort → server-side (the column needs an OrderBy on the BE);
// · client-side table (no onSort and no customPagination) → client-side sorting, with
//   a comparator that extracts values out of React cells (see sorting.ts);
// · server-paginated table WITHOUT onSort → not sortable (it would only order the
//   current page, which is misleading).
// A row value React can actually render: primitives, elements, or arrays of those.
// Plain data objects (e.g. a raw BE field surfaced by a server column config the page
// does not map yet) are NOT renderable — React would throw "Objects are not valid as a
// React child". Those cells degrade to a dash instead of crashing the page.
const isRenderableValue = (value: unknown): boolean => {
	if (value == null || typeof value !== 'object') return true;
	if (isValidElement(value)) return true;
	if (Array.isArray(value)) return value.every((item) => isRenderableValue(item));
	return false;
};

const toColDef = (
	col: IColumn,
	serverSort: boolean,
	clientSort: boolean,
	resolvedWidth?: number
): GridColDef => {
	const def: GridColDef = {
		field: col.key,
		headerName: col.label,
		align: col.align,
		// Actions column: the title sits on the left of the header (the ⋮ menu stays
		// pushed to the right), while cell content (the buttons) aligns right via `align`.
		headerAlign: col.key === 'actions' ? 'left' : col.align,
		resizable: true,
		sortable: Boolean(col.sortable) && (serverSort || clientSort),
		renderCell: (params) => {
			const value = params.row[col.key];
			// Actions cells hold buttons with their own tooltips — no overflow wrapper.
			if (col.key === 'actions') return (value as ReactNode) ?? null;
			let child: ReactNode;
			if (typeof value === 'string') {
				child = (
					<Typography variant="body2" noWrap>
						{value}
					</Typography>
				);
			} else if (isRenderableValue(value)) {
				child = (value as ReactNode) ?? null;
			} else {
				child = (
					<Typography variant="body2" noWrap>
						—
					</Typography>
				);
			}
			return <CellOverflowTooltip title={cellTextOf(value)}>{child}</CellOverflowTooltip>;
		}
	};
	if (clientSort) def.sortComparator = reactNodeComparator;
	def.width =
		typeof col.width === 'number' ? col.width : (resolvedWidth ?? MIN_COLUMN_WIDTH);
	// Resize drag bounds; computed / declared widths remain valid even outside the
	// range (the bounds stretch up to them instead of forcing them). The actions
	// column uses its own compact range.
	const isActions = col.key === 'actions';
	def.minWidth = Math.min(isActions ? ACTIONS_MIN_WIDTH : MIN_RESIZE_WIDTH, def.width);
	def.maxWidth = Math.max(isActions ? ACTIONS_MAX_WIDTH : MAX_RESIZE_WIDTH, def.width);
	return def;
};

export const CustomTable: FC<ICustomTableProps> = ({
	data,
	loading = false,
	loadingColumns = false,
	pagination = false,
	customPagination,
	isNarrow = false,
	itemsPerPage,
	toolbar,
	fullHeight = false,
	sort,
	onSort,
	disableSortRestore = false,
	emptyMessage,
	rowHeight,
	storageKey
}) => {
	const apiRef = useGridApiRef();
	const location = useLocation();
	const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage || 10);
	const [page, setPage] = useState(0);

	// Saved column configuration for this table — kept in redux (the `tables` slice,
	// cleared at logout), keyed by the page route or by storageKey / TABLE_KEYS.*.
	const configKey = storageKey ?? location.pathname;
	const { config, saveConfig, clearConfig } = useTableConfig(configKey);
	// Mount-time snapshot — the source of the initial states (afterwards the states own
	// the truth). Sanitized: it comes from redux-persist (localStorage) = external input.
	const [storedConfig] = useState(() => sanitizeTableConfig(config));

	// Real container width — the source of the proportional column computation.
	// One-way flow (container → columns): the container width does not depend on the
	// grid content, so no measurement loop can form.
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [containerWidth, setContainerWidth] = useState(0);

	useEffect(() => {
		const el = containerRef.current;
		if (!el || typeof ResizeObserver === 'undefined') return undefined;
		const update = (): void => {
			const width = Math.floor(el.clientWidth);
			setContainerWidth((prev) => (prev === width ? prev : width));
		};
		update();
		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const serverSort = Boolean(onSort);
	// Client-side sorting only when the full data set lives in the client (no BE paging).
	const clientSort = !serverSort && !customPagination;

	// ----- column pinning (left / right) — restored from the saved configuration -----
	const [pinned, setPinned] = useState<PinnedColumns>(
		storedConfig.pinned ?? { left: [], right: [] }
	);
	// Pinned columns have a frozen width (deterministic sticky offsets); on unpin they
	// return to the proportional width from their definition.
	const [pinnedWidths, setPinnedWidths] = useState<Record<string, number>>(
		storedConfig.pinnedWidths ?? {}
	);
	// Widths set manually by the user (separator drag) — they take precedence over the
	// proportional computation; the ones emitted during the pin autosize are ignored.
	const [userWidths, setUserWidths] = useState<Record<string, number>>(
		storedConfig.userWidths ?? {}
	);
	const autosizingRef = useRef(false);

	// On pin, the column starts from its current normal width and only grows if its
	// content requires it (autosizeColumns — an MIT feature; scrollWidth sees the full
	// text behind the "…"), capped at PINNED_MAX_WIDTH. It never shrinks.
	const pinColumn = useCallback(
		(field: string, side: PinnedSide): void => {
			const normalWidth = apiRef.current?.getColumn(field)?.computedWidth;
			autosizingRef.current = true;
			const freeze = (): void => {
				autosizingRef.current = false;
				const contentWidth = apiRef.current?.getColumn(field)?.computedWidth;
				setPinnedWidths((prev) => {
					const base =
						normalWidth && normalWidth > 0
							? normalWidth
							: (prev[field] ?? MIN_COLUMN_WIDTH);
					const extended = contentWidth && contentWidth > base ? contentWidth : base;
					const next = Math.min(extended, PINNED_MAX_WIDTH);
					return prev[field] === next ? prev : { ...prev, [field]: next };
				});
				setPinned((prev) => ({
					left:
						side === 'left'
							? [...prev.left.filter((f) => f !== field), field]
							: prev.left.filter((f) => f !== field),
					right:
						side === 'right'
							? [field, ...prev.right.filter((f) => f !== field)]
							: prev.right.filter((f) => f !== field)
				}));
			};
			const autosize = apiRef.current?.autosizeColumns({
				columns: [field],
				includeHeaders: true,
				includeOutliers: true
			});
			if (autosize) autosize.then(freeze).catch(freeze);
			else freeze();
		},
		[apiRef]
	);

	const unpinColumn = useCallback((field: string): void => {
		setPinned((prev) => ({
			left: prev.left.filter((f) => f !== field),
			right: prev.right.filter((f) => f !== field)
		}));
	}, []);

	// Pinning drives the grid's NATIVE pipeline: we write the internal `pinnedColumns`
	// state (MIT does not expose the prop — that is Pro — but the state exists and the
	// renderer honors it fully). Pinned cells are therefore positioned with offsets
	// synchronized in the same frame as the grid's transform-emulated scrolling —
	// hand-written sticky CSS visibly lagged behind on fast scrolls.
	useEffect(() => {
		apiRef.current?.setState((state) => ({
			...state,
			pinnedColumns: { left: pinned.left, right: pinned.right }
		}));
	}, [apiRef, pinned]);

	const hasRows = (data.rows?.length ?? 0) > 0;

	// DataGrid requires a unique row id; page rows are plain objects → internal id.
	// `__rowId` is set AFTER the spread — a row that happens to contain the key (e.g. a
	// spread BE object) must not override the id (duplicate ids crash the grid).
	// While loading with no data yet, one page of empty placeholder rows is fed to the
	// grid: the skeleton overlay sizes itself from the viewport height (rows count =
	// viewport / rowHeight), and with autoHeight + zero rows the viewport is ~0px, so
	// the skeleton would collapse to a thin sliver instead of filling a page.
	// While the column configuration loads, the body must show the skeleton as well —
	// the rows would otherwise render against columns that are about to change.
	const isLoading = loading || loadingColumns;

	const gridRows: GridValidRowModel[] = useMemo(() => {
		const source = data.rows ?? [];
		if (isLoading && source.length === 0) {
			return Array.from({ length: 5 }, (_, index) => ({ __rowId: index }));
		}
		return source.map((row, index: number) => ({ ...row, __rowId: index }));
	}, [data.rows, isLoading, itemsPerPage, rowsPerPage]);

	// Column visibility (the "Manage columns" menu) is controlled here so widths are
	// recomputed over the VISIBLE columns only — with 4 visible columns they fill the
	// whole container, no free space on the right.
	const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>(
		storedConfig.visibility ?? {}
	);

	// Server-driven defaults: columns flagged `defaultHidden` (visible: false in a BE
	// table-config) start hidden — but only where the user has no choice yet. Stored /
	// session visibility entries always win, so the "Manage columns" menu keeps working
	// exactly as before on top of the BE defaults.
	useEffect(() => {
		setColumnVisibility((prev) => {
			let changed = false;
			const next = { ...prev };
			data.columns.forEach((col) => {
				if (col.defaultHidden && next[col.key] === undefined) {
					next[col.key] = false;
					changed = true;
				}
			});
			return changed ? next : prev;
		});
	}, [data.columns]);

	const visibleColumns = useMemo(
		() => data.columns.filter((col) => columnVisibility[col.key] !== false),
		[data.columns, columnVisibility]
	);

	// Proportional widths in px: each VISIBLE column's percentage relative to the sum of
	// visible percentages, applied to the space left after "fixed"-width columns —
	// explicit px from the definition, manually dragged widths, or pinned columns with a
	// frozen width. Available space is therefore always fully occupied: after a manual
	// resize the remaining columns stretch / shrink over what is left. Distribution is
	// pixel-exact (floor + remainder by largest fraction) — naive rounding overshot the
	// container by 1-3px and produced a phantom horizontal scrollbar. The per-column
	// floor is applied last (below it → intentional scroll) and depends on interaction:
	// pristine tables use PRISTINE_MIN_COLUMN_WIDTH so the DEFAULT column set never
	// starts with a scrollbar; after resize / extra columns / pinning the comfortable
	// MIN_COLUMN_WIDTH takes over. A column's declared `minWidth` always wins.
	const percentWidths = useMemo(() => {
		const width = containerWidth || 1200; // provisional, until the first measurement
		let fixedTotal = 0;
		// Only valid widths (> 0) — a corrupted/zero value must not remove the column
		// from the proportional computation.
		const positive = (value?: number): number | undefined =>
			typeof value === 'number' && value > 0 ? value : undefined;
		const frozenWidthOf = (key: string): number | undefined => {
			if (pinned.left.includes(key) || pinned.right.includes(key)) {
				return positive(pinnedWidths[key]) ?? positive(userWidths[key]);
			}
			return positive(userWidths[key]);
		};
		const rawPercents: (number | null)[] = visibleColumns.map((col) => {
			const frozen = frozenWidthOf(col.key);
			if (frozen) {
				fixedTotal += frozen;
				return null;
			}
			if (typeof col.width === 'number') {
				fixedTotal += col.width;
				return null;
			}
			const pct = parseFloat(String(col.width));
			return Number.isNaN(pct) || pct <= 0 ? 100 / visibleColumns.length : pct;
		});
		const result: Record<string, number> = {};
		// Actions column: its proportional share is clamped to a compact fixed width
		// (small single-row buttons); the freed space is redistributed to the content
		// columns below.
		const rawTotalPct =
			rawPercents.reduce<number>((acc, pct) => acc + (pct ?? 0), 0) || 100;
		const percents = rawPercents.map((pct, index) => {
			if (pct === null || visibleColumns[index].key !== 'actions') return pct;
			const share = (Math.max(width - fixedTotal, 0) * pct) / rawTotalPct;
			const fixed = Math.min(
				Math.max(Math.round(share), ACTIONS_MIN_WIDTH),
				ACTIONS_MAX_WIDTH
			);
			result[visibleColumns[index].key] = fixed;
			fixedTotal += fixed;
			return null;
		});
		const totalPct = percents.reduce<number>((acc, pct) => acc + (pct ?? 0), 0) || 100;
		const available = Math.max(width - fixedTotal, 0);
		const fractions: { key: string; frac: number }[] = [];
		let used = 0;
		visibleColumns.forEach((col, index) => {
			const pct = percents[index];
			if (pct === null) return;
			const exact = (available * pct) / totalPct;
			const floor = Math.floor(exact);
			result[col.key] = floor;
			used += floor;
			fractions.push({ key: col.key, frac: exact - floor });
		});
		let rest = available - used;
		fractions.sort((a, b) => b.frac - a.frac);
		for (let i = 0; i < fractions.length && rest > 0; i += 1) {
			result[fractions[i].key] += 1;
			rest -= 1;
		}
		// With no data the minimum is skipped: an empty table has nothing to scroll —
		// columns shrink to fit the container exactly (headers ellipsize). The actions
		// column keeps its own compact clamp regardless.
		if (hasRows) {
			// "Interacted" = manual resize, pinning, or a default-hidden column turned on
			// from "Manage columns" — only then is a horizontal scrollbar acceptable.
			const interacted =
				Object.keys(userWidths).length > 0 ||
				pinned.left.length > 0 ||
				pinned.right.length > 0 ||
				visibleColumns.some((col) => col.defaultHidden);
			const defaultFloor = interacted ? MIN_COLUMN_WIDTH : PRISTINE_MIN_COLUMN_WIDTH;
			visibleColumns.forEach((col) => {
				if (col.key === 'actions' || result[col.key] === undefined) return;
				result[col.key] = Math.max(result[col.key], col.minWidth ?? defaultFloor);
			});
		}
		return result;
	}, [visibleColumns, containerWidth, userWidths, pinned, pinnedWidths, hasRows]);

	// Pinned columns are moved to the edges of the column list (the grid's internal
	// index math expects pinned sections at the edges) and get a frozen width so the
	// sticky offsets stay stable.
	// Depends on the `serverSort` boolean, not on the `onSort` function (new identity on
	// every page render) — otherwise `columns` would be re-created constantly and the
	// grid would re-run its column processing on every render.
	const columns: GridColDef[] = useMemo(() => {
		const defs = data.columns.map((col) => {
			const def = toColDef(col, serverSort, clientSort, percentWidths[col.key]);
			const userWidth = userWidths[col.key];
			if (typeof userWidth === 'number' && userWidth > 0) def.width = userWidth;
			if (pinned.left.includes(col.key) || pinned.right.includes(col.key)) {
				const pinnedWidth = pinnedWidths[col.key];
				if (typeof pinnedWidth === 'number' && pinnedWidth > 0) def.width = pinnedWidth;
			}
			// Column-config loading: headers render as skeletons (labels/order may still
			// change once the server configuration lands) and stay inert.
			if (loadingColumns) {
				def.renderHeader = () => <Skeleton width="60%" sx={{ maxWidth: 96 }} />;
				def.sortable = false;
			}
			return def;
		});
		const byField = Object.fromEntries(defs.map((def) => [def.field, def]));
		const leftDefs = pinned.left.map((f) => byField[f]).filter(Boolean);
		const rightDefs = pinned.right.map((f) => byField[f]).filter(Boolean);
		const middleDefs = defs.filter(
			(def) => !pinned.left.includes(def.field) && !pinned.right.includes(def.field)
		);
		// Default position of the actions column: last, at the far right. Whatever order a
		// (server) column config gives it, and whichever hidden columns the user toggles on
		// from "Manage columns", `actions` stays the last non-pinned column — otherwise a
		// column ordered after it would render to its right. If the user explicitly pins it
		// left/right it lands in `leftDefs`/`rightDefs` instead and this reorder skips it.
		const actionsIdx = middleDefs.findIndex((def) => def.field === 'actions');
		if (actionsIdx !== -1) {
			middleDefs.push(middleDefs.splice(actionsIdx, 1)[0]);
		}
		const ordered = [...leftDefs, ...middleDefs, ...rightDefs];
		// Fill invariant — the visible columns must always cover the container. The
		// proportional engine is pixel-exact, but a configuration can leave NOTHING
		// flexible (every content column manually resized / pinned, plus the clamped
		// actions column): the leftover then shows as an empty gap after the last
		// column. Any shortfall stretches the last visible content column (never
		// `actions`, never a pinned column — their offsets are frozen).
		const gridWidth = containerWidth || 1200;
		const visibleKeys = new Set(visibleColumns.map((col) => col.key));
		const totalVisible = ordered.reduce(
			(acc, def) => acc + (visibleKeys.has(def.field) ? (def.width ?? 0) : 0),
			0
		);
		const shortfall = gridWidth - totalVisible;
		if (shortfall > 0) {
			const absorber = [...middleDefs]
				.reverse()
				.find((def) => def.field !== 'actions' && visibleKeys.has(def.field));
			if (absorber) {
				absorber.width = (absorber.width ?? 0) + shortfall;
				// The resize bounds were derived from the pre-stretch width — lift the cap
				// so the grid does not clamp the stretched width back down.
				absorber.maxWidth = Math.max(absorber.maxWidth ?? 0, absorber.width);
			}
		}
		return ordered;
	}, [
		data.columns,
		visibleColumns,
		containerWidth,
		serverSort,
		clientSort,
		percentWidths,
		userWidths,
		pinned,
		pinnedWidths,
		loadingColumns
	]);

	// Client-side sorting is controlled internally so it can be restored from config.
	const [clientSortModel, setClientSortModel] = useState<GridSortModel>(() => {
		const stored = storedConfig.sort;
		if (!stored) return [];
		const col = data.columns.find(
			(c) => (c.sortKey ?? c.key) === stored.field && c.sortable
		);
		return col ? [{ field: col.key, sort: stored.direction }] : [];
	});

	// Server-side sort restore: once, at mount — the page is notified through onSort
	// (once → asc, twice → desc; pages use a functional-updater toggle and React batches
	// both set calls into a single refetch).
	const restoredSortRef = useRef(false);
	useEffect(() => {
		if (restoredSortRef.current) return;
		restoredSortRef.current = true;
		// URL-driven pages (useListQueryState) restore the sort from the query string —
		// replaying the redux one here would override a shared link's sort.
		if (disableSortRestore) return;
		const stored = storedConfig.sort;
		if (!stored || !onSort) return;
		// Cross-instance idempotency: if another mount on the same key has just replayed
		// the sort, we do not replay it again (a double replay would flip the direction).
		const lastRestore = restoredSortAt.get(configKey) ?? 0;
		if (Date.now() - lastRestore < RESTORE_REPLAY_WINDOW_MS) return;
		const col = data.columns.find(
			(c) => (c.sortKey ?? c.key) === stored.field && c.sortable
		);
		if (!col) return;
		if (sort?.field === stored.field && sort.direction === stored.direction) return;
		restoredSortAt.set(configKey, Date.now());
		if (sort?.field !== stored.field) {
			onSort(stored.field);
			if (stored.direction === 'desc') onSort(stored.field);
		} else {
			onSort(stored.field);
		}
	}, [storedConfig, configKey, onSort, sort, data.columns, disableSortRestore]);

	// The sort to persist: on server tables it is the `sort` prop (page state), on
	// client tables it is the grid's internal model (mapped back to the column sortKey).
	const persistedSort = useMemo(() => {
		if (onSort)
			return sort ? { field: sort.field, direction: sort.direction } : undefined;
		const model = clientSortModel[0];
		if (!model?.sort) return undefined;
		const col = data.columns.find((c) => c.key === model.field);
		return { field: col?.sortKey ?? model.field, direction: model.sort };
	}, [onSort, sort, clientSortModel, data.columns]);

	// Persist visibility, sort, manual widths and pinning on every change, but ONLY for
	// columns that exist right now — entries left behind by renamed/removed columns
	// would pile up forever and keep the config "non-default" indefinitely. On the
	// default configuration the key is removed so the slice stays clean.
	useEffect(() => {
		const columnKeys = new Set(data.columns.map((col) => col.key));
		const keepKnown = <T,>(rec: Record<string, T>): Record<string, T> =>
			Object.fromEntries(Object.entries(rec).filter(([key]) => columnKeys.has(key)));

		const visibility = keepKnown(columnVisibility);
		const widths = keepKnown(userWidths);
		const frozenWidths = keepKnown(pinnedWidths);
		const pinnedClean = {
			left: pinned.left.filter((key) => columnKeys.has(key)),
			right: pinned.right.filter((key) => columnKeys.has(key))
		};

		const isDefault =
			Object.keys(visibility).length === 0 &&
			!persistedSort &&
			Object.keys(widths).length === 0 &&
			pinnedClean.left.length === 0 &&
			pinnedClean.right.length === 0;
		if (isDefault) {
			clearConfig();
			return;
		}
		saveConfig({
			visibility,
			sort: persistedSort,
			userWidths: widths,
			pinned: pinnedClean,
			pinnedWidths: frozenWidths
		});
	}, [
		saveConfig,
		clearConfig,
		data.columns,
		columnVisibility,
		persistedSort,
		userWidths,
		pinned,
		pinnedWidths
	]);

	// Server-side sorting: the model is controlled by the `sort` prop; a header click
	// only notifies the page (onSort), which decides the direction and refetches.
	const sortModel: GridSortModel = useMemo(() => {
		if (!sort) return [];
		const col = data.columns.find((c) => (c.sortKey ?? c.key) === sort.field);
		// An active sort on a field that is NOT a column (e.g. OrderBy=position, the
		// default of some lists) must not enter the controlled model: the grid would
		// strip it on every columns change and emit sortModelChange → onSort → setState
		// → infinite loop ("Maximum update depth exceeded" right after data loads).
		if (!col) return [];
		return [{ field: col.key, sort: sort.direction }];
	}, [sort, data.columns]);

	const handleSortModelChange = useCallback(
		(model: GridSortModel): void => {
			if (!onSort) return;
			// On the third click the grid cycles to "unsorted" (empty model) — we stay on
			// the same column so the page keeps toggling it.
			const clickedField = model[0]?.field ?? sortModel[0]?.field;
			if (!clickedField) return;
			const col = data.columns.find((c) => c.key === clickedField);
			onSort(col?.sortKey ?? clickedField);
		},
		[onSort, sortModel, data.columns]
	);

	const handlePaginationModelChange = useCallback(
		(model: { page: number; pageSize: number }): void => {
			if (pagination && !customPagination) setPage(model.page);
		},
		[pagination, customPagination]
	);

	const handleColumnWidthChange = useCallback(
		(params: { colDef: { field: string }; width: number }): void => {
			// User resizes only; the autosize emitted while pinning is ignored.
			if (autosizingRef.current) return;
			const { field } = params.colDef;
			if (pinned.left.includes(field) || pinned.right.includes(field)) {
				setPinnedWidths((prev) =>
					prev[field] === params.width ? prev : { ...prev, [field]: params.width }
				);
			} else {
				setUserWidths((prev) =>
					prev[field] === params.width ? prev : { ...prev, [field]: params.width }
				);
			}
		},
		[pinned]
	);

	// Row height: 44/52 (Mantis) or the page's value (multi-line cells).
	const effectiveRowHeight = rowHeight ?? (isNarrow ? 44 : 52);

	// Pagination: with `customPagination` the data already comes paginated from the BE
	// (everything is shown); without it, DataGrid slices client-side, controlled by the
	// Mantis pagination below the table. Note: the MIT grid renders at most 100 rows
	// per page.
	const clientPagination = pagination && !customPagination;
	const pageSize = itemsPerPage || rowsPerPage;
	const paginationModel = clientPagination
		? { page, pageSize }
		: { page: 0, pageSize: 100 };

	const handleChangeRowsPerPage = (
		event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
	): void => {
		setRowsPerPage(parseInt(event.target.value));
		setPage(0);
	};

	useEffect(() => {
		loading && setPage(0);
	}, [loading]);

	// Grid styling, memoized — the sx object would otherwise get a new identity on every
	// render and force emotion to re-serialize the whole style set each time.
	const gridSx = useMemo(
		() => (theme: Theme) => ({
			border: 'none',
			borderRadius: 0,
			fontSize: '0.875rem',
			// Header — un pic mai gri (grey.100) ca în template, cu bordurile de divider.
			'--DataGrid-containerBackground': theme.vars.palette.grey[50],
			'--DataGrid-rowBorderColor': theme.vars.palette.divider,
			// Antet gri (grey.100, ca în template). Celulele de antet sunt transparente în
			// DataGrid v9, deci var-ul de container nu se vede — colorăm explicit rândul de
			// antet ȘI fiecare celulă.
			'& .MuiDataGrid-columnHeaders [role=row]': {
				borderTop: `1px solid ${theme.vars.palette.divider}`,
				backgroundColor: theme.vars.palette.grey[50]
			},
			'& .MuiDataGrid-columnHeader': {
				padding: '0 8px',
				backgroundColor: theme.vars.palette.grey[50]
			},
			'& .MuiDataGrid-columnHeaderTitle': {
				fontWeight: 700,
				fontSize: '0.75rem',
				textTransform: 'uppercase' as const
			},
			'& .MuiDataGrid-columnSeparator': { color: theme.vars.palette.divider },
			// Cells — 12px padding, vertically centered, min-height like the Mantis rows.
			'& .MuiDataGrid-cell': {
				display: 'flex',
				alignItems: 'center',
				padding: '12px',
				minHeight: effectiveRowHeight,
				overflow: 'hidden'
			},
			// Cells are flex containers → the horizontal alignment from `align`
			// (right/center) must be applied through justifyContent, otherwise content
			// (e.g. the action buttons) stays glued to the left regardless of alignment.
			'& .MuiDataGrid-cell--textRight': { justifyContent: 'flex-end' },
			'& .MuiDataGrid-cell--textCenter': { justifyContent: 'center' },
			// Actions cells: compact buttons, always on a single row, right-aligned —
			// the wrapper layout is normalized here so it cannot wrap, squish or
			// misalign regardless of the page-side container (Stack / Flex / Box).
			'& .MuiDataGrid-cell[data-field="actions"] > *': {
				display: 'flex',
				flexDirection: 'row' as const,
				flexWrap: 'nowrap' as const,
				alignItems: 'center',
				justifyContent: 'flex-end',
				flexShrink: 0
			},
			'& .MuiDataGrid-cell[data-field="actions"] .MuiIconButton-root': {
				width: 30,
				height: 30,
				minWidth: 30,
				flexShrink: 0
			},
			// Cells are flex containers → children do not shrink below their content
			// width by default (min-width:auto), which disabled the "…" ellipsis; we
			// constrain them to the column width, like the old <td> did. The same rule
			// applies inside the CellOverflowTooltip wrapper (.cz-cell-content).
			'& .MuiDataGrid-cell > *': { minWidth: 0, maxWidth: '100%' },
			'& .cz-cell-content > *': { minWidth: 0, maxWidth: '100%' },
			// Plain text (Typography / Link) placed in a cell — single line with "…" on
			// overflow; rows have a fixed height, so long text would otherwise wrap and
			// get clipped vertically. Composite layouts (Stack / Box with two lines) are
			// unaffected — they bring their own wrapper.
			'& .MuiDataGrid-cell > .MuiTypography-root, & .cz-cell-content > .MuiTypography-root':
				{
					whiteSpace: 'nowrap' as const,
					overflow: 'hidden',
					textOverflow: 'ellipsis'
				},
			// No focus outline for pointer interactions, but KEEP a visible outline for
			// keyboard navigation (:focus-visible) — accessibility.
			'& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
				outline: 'none'
			},
			'& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
				outline: 'none'
			},
			'& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible': {
				outline: `1px solid ${theme.vars.palette.primary.main}`,
				outlineOffset: -1
			},
			'& .MuiDataGrid-overlayWrapper': { minHeight: 220 },
			'& .MuiDataGrid-overlayWrapperInner': { minHeight: 220 },
			// Skeleton bars: the MUI text variant scales itself down to ~60% of the text
			// height, which reads as tiny lines inside 52px rows — give them a real height.
			'& .MuiDataGrid-cellSkeleton .MuiSkeleton-root': {
				height: 24,
				transform: 'none',
				borderRadius: 1.5
			},
			// Pinned columns (native pipeline) — opaque Mantis-style backgrounds:
			// grey.100 headers (ca antetul), paper cells, solid tint on row hover.
			'& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
				backgroundColor: theme.vars.palette.background.paper
			},
			'& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight':
				{ backgroundColor: theme.vars.palette.grey[50] },
			'& .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedRight':
				{ backgroundColor: theme.vars.palette.grey[50] }
		}),
		[effectiveRowHeight]
	);

	return (
		<TableCardWrap sx={fullHeight ? { flexGrow: 1, minHeight: 0 } : undefined}>
			{toolbar && (
				<Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
					{toolbar}
				</Box>
			)}
			<Box
				ref={containerRef}
				sx={{ width: '100%', ...(fullHeight ? { flexGrow: 1, minHeight: 0 } : {}) }}
			>
				<DataGrid
					apiRef={apiRef}
					rows={gridRows}
					columns={columns}
					getRowId={getRowId}
					loading={isLoading}
					localeText={localeText}
					columnHeaderHeight={48}
					// Heights: `autoHeight` (intrinsic, content-based height) must NOT be
					// combined with `getRowHeight: 'auto'` — together they enter a measure
					// loop (Maximum update depth). Regular tables therefore use fixed rows
					// (44/52 or the page's `rowHeight`), and only `fullHeight` tables (flex
					// bounded viewport) measure their rows from content.
					{...(fullHeight
						? { getRowHeight: () => 'auto' as const }
						: { autoHeight: true, rowHeight: effectiveRowHeight })}
					{...(onSort
						? {
								sortingMode: 'server' as const,
								sortModel,
								onSortModelChange: handleSortModelChange
							}
						: {
								sortingMode: 'client' as const,
								sortModel: clientSortModel,
								onSortModelChange: setClientSortModel
							})}
					columnVisibilityModel={columnVisibility}
					onColumnVisibilityModelChange={setColumnVisibility}
					paginationModel={paginationModel}
					onPaginationModelChange={handlePaginationModelChange}
					onColumnWidthChange={handleColumnWidthChange}
					hideFooter
					disableColumnFilter
					disableRowSelectionOnClick
					slots={{ noRowsOverlay: NoRowsOverlay, columnMenu: ColumnMenuWithPinning }}
					slotProps={{
						loadingOverlay: {
							variant: 'skeleton',
							noRowsVariant: 'skeleton',
							style: { minHeight: 36 }
						},
						noRowsOverlay: { message: emptyMessage },
						columnMenu: {
							pinnedColumns: pinned,
							onPinColumn: pinColumn,
							onUnpinColumn: unpinColumn
						}
					}}
					sx={gridSx}
				/>
			</Box>
			{pagination && (
				<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
					<CustomTablePagination
						totalRows={data.rows?.length || 0}
						rowsPerPage={pageSize}
						handleChangeRowsPerPage={handleChangeRowsPerPage}
						page={page}
						handleChangePage={(
							_: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
							newPage: number
						) => setPage(newPage)}
						customComponent={customPagination}
					/>
				</Box>
			)}
		</TableCardWrap>
	);
};
